/**
 * Returns true when the event originates from a form input element
 * (`<input>`, `<textarea>` or `<select>`). Used to skip global shortcuts
 * while the user is typing.
 */
export function isFormFieldTarget(e: Event): boolean {
  const tag = (e.target as HTMLElement)?.tagName?.toLowerCase()
  return tag === 'input' || tag === 'textarea' || tag === 'select'
}

/** True while any modal dialog (an `[aria-modal="true"]` element) is in the DOM. */
export function isModalOpen(): boolean {
  return !!document.querySelector('[aria-modal="true"]')
}

/** True when `e` landed anywhere inside the Markup editor's work pane (`.work-pane`). */
export function isWorkPaneTarget(e: Event): boolean {
  const el = e.target as Element | null
  return el?.closest?.('.work-pane') != null
}

/** The Markup work pane's controls: Delete/Backspace on one of them is not meant for the map (D1). */
const WORK_PANE_CONTROL = 'button, [role="option"], [role="tab"], [role="switch"], a[href]'

/**
 * True when `e` landed inside an editing sidebar (the shared attribute sidebar
 * with its Yes/No toggle buttons, or the Markup annotation sidebar), on a
 * control of the Markup editor's work pane (a button — Apply, the pager, a
 * footer button —, a list row, a tab or the Auto-next switch) or on a
 * contenteditable element. Destructive shortcuts (Delete/Backspace) must not
 * act there: the user means to edit that control, not to delete the selected
 * feature. The rest of the work pane — the detail heading a row pick moves
 * focus to, the list container, plain text — is not matched, so a keyboard
 * user can still press Delete there (the Markup editor refuses Backspace
 * anywhere in the pane itself, `isWorkPaneTarget`). Toolbar buttons are deliberately not
 * matched either, so Delete still works right after a toolbar click.
 */
export function isFocusedControlTarget(e: Event): boolean {
  const el = e.target as HTMLElement | null
  if (!el?.closest) return false
  return el.isContentEditable === true ||
    el.closest('[contenteditable="true"], app-attribute-sidebar, app-markup-annotation-sidebar') !== null ||
    (isWorkPaneTarget(e) && el.closest(WORK_PANE_CONTROL) !== null)
}

/**
 * True when a global single-key shortcut listener should ignore `e`: an
 * auto-repeat or IME-composition keystroke, any modifier held, the event
 * landed in a form field, an open modal (the shared dialog shell is a plain
 * div, so a document-level listener still fires under it), or an open menu
 * within `scope` (a trigger marked `data-blocks-shortcuts` with
 * `[aria-expanded="true"]`, e.g. the app bar's account menu). `scope` is
 * normally the listening component's own host element — narrower than the
 * whole document, since a menu open elsewhere on the page is not this
 * screen's concern. Any other expanded control (an open More, an expanded
 * section) does not count: it takes no keys of its own, so the
 * screen's shortcuts must keep working while it is open.
 *
 * `allowShift` lets Shift through (every other modifier still counts) — for
 * a shortcut that is a shifted character on most layouts, such as `?`. Pass
 * it only when matching that one key: a letter shortcut must keep ignoring
 * Shift, or Shift+Y would answer.
 *
 * Shared by every full-screen shortcut listener built the same way: guard
 * once, then dispatch on `e.key`.
 */
export function shouldIgnoreShortcut(
  e: KeyboardEvent, scope: Element, opts: { allowShift?: boolean } = {},
): boolean {
  return shortcutIgnoreReason(e, scope, opts) !== null
}

/** Why a global shortcut listener ignores a key (see `shouldIgnoreShortcut`); null = act on it. */
export type ShortcutIgnoreReason = 'repeat' | 'composing' | 'modifier' | 'form_field' | 'modal' | 'menu_open'

/**
 * The reason `shouldIgnoreShortcut` is true, or null. Same checks in the same
 * order; `shouldIgnoreShortcut` delegates here, so behaviour is unchanged. Lets a
 * caller report *why* a key was refused (Editor Activity Tracking).
 */
export function shortcutIgnoreReason(
  e: KeyboardEvent, scope: Element, { allowShift = false }: { allowShift?: boolean } = {},
): ShortcutIgnoreReason | null {
  if (e.repeat) return 'repeat'
  if (e.isComposing) return 'composing'
  if (e.ctrlKey || e.metaKey || e.altKey) return 'modifier'
  if (e.shiftKey && !allowShift) return 'modifier'
  if (isFormFieldTarget(e)) return 'form_field'
  if (isModalOpen()) return 'modal'
  if (scope.querySelector('[data-blocks-shortcuts][aria-expanded="true"]')) return 'menu_open'
  return null
}
